/**
 * MsrTabs — the MSR portal's top tab bar, shared across the portal pages so a new
 * tab is added in one place. `active` selects the current tab; the others are links.
 */
import React from "react";
import { Link } from "react-router-dom";
import NotificationBell from "../notifications/NotificationBell";
import { useAuth } from "../../contexts/AuthContext";
import { normalizeRole } from "../../utils/roleUtils";
import MsrAvailabilityPrompt from "./MsrAvailabilityPrompt";

type MsrTabKey =
  | "schedule"
  | "member-lookup"
  | "availability"
  | "field-sale"
  | "sales-history"
  | "commission";

// Member Lookup is shown only to roles the route and the RPC permit (admin/superadmin). MSR plan slice 1 hid it
// for MSRs; round-2 audit MSR2-M1: admins must keep the link, not just the route.
const MEMBER_LOOKUP_ROLES = new Set(["admin", "superadmin"]);

const TABS: {
  key: MsrTabKey;
  label: string;
  to: string;
  roles?: Set<string>;
}[] = [
  { key: "schedule", label: "Schedule", to: "/msr" },
  {
    key: "member-lookup",
    label: "Member Lookup",
    to: "/msr/member-lookup",
    roles: MEMBER_LOOKUP_ROLES,
  },
  { key: "availability", label: "My Availability", to: "/msr/availability" },
  { key: "field-sale", label: "Verify a Sale", to: "/msr/verify-sale" },
  { key: "sales-history", label: "Sales History", to: "/msr/sales-history" },
  { key: "commission", label: "Commission", to: "/commission-estimator" },
  // "Booth Check-in" / "Event Check-in" tabs removed 2026-08 (login-gated + confusing;
  // public check-in is /self-checkin, assisted check-in is per-event). Routes still exist.
];

const MsrTabs: React.FC<{ active: MsrTabKey }> = ({ active }) => {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const tabs = TABS.filter((t) => !t.roles || t.roles.has(role));
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <nav className="flex flex-wrap gap-2 text-sm">
          {tabs.map((t) =>
            t.key === active ? (
              <span
                key={t.key}
                className="rounded-full bg-blue-600 px-3 py-1 font-medium text-white"
              >
                {t.label}
              </span>
            ) : (
              <Link
                key={t.key}
                to={t.to}
                className="rounded-full bg-gray-100 px-3 py-1 font-medium text-gray-700 hover:bg-gray-200"
              >
                {t.label}
              </Link>
            ),
          )}
        </nav>
        <div className="flex-shrink-0">
          <NotificationBell />
        </div>
      </div>
      {/* The one availability nudge (business lead, 2026-10-27): next month's availability from the prompt day on.
          Replaces the "calendar confirmed through" banner. Shown on every portal page except My Availability (which IS the place to do it). */}
      {role === "msr" && active !== "availability" && <MsrAvailabilityPrompt />}
    </>
  );
};

export default MsrTabs;
